import { events } from "@constants/tracking";
import useAIFeedbackStore from "@features/session/hooks/useAIFeedbackStore";
import { isWeb } from "@helpers/platform";
import { TTSContainer } from "@memorang/features/src/TTS";
import { Box, TextSkeleton } from "@memorang/ui";
import { trackCustomEvent } from "analytics";
import CustomListSubheaderContainer from "components/CustomListSubheaderContainer";
import CustomMarkdownRenderer from "components/CustomMarkdownRenderer";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AIFeedbackMenu } from "../AIFeedbackMenu";

type Props = {
	aiResponse?: string;
	loading?: boolean;
	itemId: number;
};
const Wrapper = isWeb ? "div" : Box;

const AIExplanation = ({ aiResponse, loading, itemId }: Props) => {
	const explanationContainerRef = useRef<HTMLDivElement | null>(null);
	const { t } = useTranslation();
	const streaming = useAIFeedbackStore((store) => store.streaming);

	useEffect(() => {
		if (aiResponse && aiResponse.length < 40 && isWeb) {
			explanationContainerRef.current?.scrollIntoView({
				behavior: "smooth",
				block: "center",
			});
		}
	}, [aiResponse]);

	if (loading && !aiResponse) {
		return (
			<Wrapper>
				<Box
					flexDirection="row"
					alignItems="center"
					justifyContent="space-between"
					height={34}
				>
					<CustomListSubheaderContainer enableGutters={false}>
						{t("session.explanation.feedback")}
					</CustomListSubheaderContainer>
				</Box>
				<TextSkeleton numLines={4} height={20} />
			</Wrapper>
		);
	}
	return (
		<Wrapper ref={explanationContainerRef}>
			<Box
				flexDirection="row"
				alignItems="center"
				justifyContent="space-between"
				paddingTop={8}
			>
				<CustomListSubheaderContainer
					enableGutters={false}
					style={{
						paddingVertical: 16,
					}}
					textVariant="titleMedium"
				>
					{t("session.explanation.feedback")}
				</CustomListSubheaderContainer>
				<Box flexDirection="row" alignItems="center" gap={6} marginRight={-12}>
					<TTSContainer
						explanation={aiResponse}
						eventName={events.ttsClicked}
						trackCustomEvent={trackCustomEvent}
						itemId={`${itemId}`}
						size={18}
						disabled={streaming}
					/>
					<AIFeedbackMenu
						itemId={itemId}
						featureName="AI Feedback"
						disabled={streaming}
					/>
				</Box>
			</Box>
			<CustomMarkdownRenderer
				text={aiResponse}
				key={aiResponse}
				variant="explanation"
				fontSize={16}
				customStyles={{
					paragraph: {
						lineHeight: 22,
						marginBottom: 8,
					},
				}}
				paragraphMarginVertical={6}
			/>
		</Wrapper>
	);
};

export default AIExplanation;
